import {
  MAINSTREAM_MARKETS,
  RADAR_METHODOLOGY_VERSION,
  STOCK_MAPPINGS,
  analyzeSwing,
  articlePlainText,
  btcRangeZone,
  classifyListingAnnouncement,
  discoverBStockUniverse,
  extractScheduleUpdateTradingAt,
  extractSpotSymbols,
  extractTradingAt,
  guardSwingSignal,
  isListingScheduleCancellation,
  isListingScheduleUpdate,
  listingEntryConfirmation,
  listingPhase,
  parseBinanceKlines
} from './strategyRadarModel.ts'
import type {
  RadarBStockUniverse,
  RadarEntryConfirmation,
  RadarListingBatch,
  RadarListingResult,
  RadarMarketBatch,
  RadarMarketResult,
  StrategyRadarSnapshot
} from './strategyRadarApi'
import type { StrategyRadarError, StrategyRadarSectionStatus } from './types'

const BINANCE_MARKET_BASE = 'https://data-api.binance.vision'
const BINANCE_CMS_BASE = 'https://www.binance.com/bapi/composite/v1/public/cms'
const BINANCE_BSTOCK_DISCOVERY_URL = 'https://www.binance.com/bapi/composite/v1/public/marketing/symbol/list'
const ANNOUNCEMENT_DETAIL_TTL_MS = 5 * 60_000
const TRADING_STATUS_TTL_MS = 2 * 60_000
const MAX_BSTOCK_MARKETS = 8
const MAX_LISTING_CONFIRMATION_SYMBOLS = 2
const announcementDetailCache = new Map<string, {
  tradingAt: number | null
  symbols: string[]
  warning: string | null
  cancelled: boolean
  fetchedAt: number
}>()
type TradingStatus = {
  status: string
  isSpotTradingAllowed: boolean
}

const tradingStatusCache = new Map<string, TradingStatus & { fetchedAt: number }>()

type BinanceArticle = {
  code?: unknown
  title?: unknown
  releaseDate?: unknown
}

export type PublicStrategyRadarLoader = (
  signal?: AbortSignal
) => Promise<StrategyRadarSnapshot>

export function resetPublicStrategyRadarCaches() {
  announcementDetailCache.clear()
  tradingStatusCache.clear()
}

export async function fetchPublicStrategyRadar(signal?: AbortSignal): Promise<StrategyRadarSnapshot> {
  const generatedAt = Date.now()
  const [marketOutcome, listingOutcome] = await Promise.allSettled([
    fetchPublicMarkets(signal),
    fetchPublicListings(signal)
  ])
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
  const marketResult = marketOutcome.status === 'fulfilled'
    ? marketOutcome.value
    : {
        batch: unavailableMarketBatch(marketOutcome.reason, generatedAt),
        universe: unavailableBStockUniverse(marketOutcome.reason, generatedAt)
      }
  const listings = listingOutcome.status === 'fulfilled'
    ? listingOutcome.value
    : unavailableListingBatch(listingOutcome.reason, generatedAt)
  return {
    methodologyVersion: RADAR_METHODOLOGY_VERSION,
    generatedAt,
    venue: 'BINANCE',
    marketType: 'spot',
    timeframe: '4h',
    researchOnly: true,
    isTradeablePrice: false,
    bStockUniverse: marketResult.universe,
    markets: marketResult.batch,
    listings,
    errors: []
  }
}

async function fetchPublicMarkets(signal?: AbortSignal): Promise<{
  batch: RadarMarketBatch
  universe: RadarBStockUniverse
}> {
  const discoveredAt = Date.now()
  const discoveryOutcome = await Promise.allSettled([
    fetchJson(BINANCE_BSTOCK_DISCOVERY_URL, signal)
  ])
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')

  let discoveryError: StrategyRadarError | null = null
  let candidateUniverse: RadarBStockUniverse
  try {
    if (discoveryOutcome[0].status === 'rejected') throw discoveryOutcome[0].reason
    const discovery = discoverBStockUniverse(discoveryOutcome[0].value, MAX_BSTOCK_MARKETS)
    if (!discovery.items.length) throw new Error('Binance 官方 bStocks 列表未返回可验证的 USDT 标的')
    candidateUniverse = {
      status: 'available',
      ...discovery,
      source: 'BINANCE_MARKETING_SYMBOL_LIST',
      fetchedAt: discoveredAt,
      warning: null
    }
  } catch (error) {
    discoveryError = radarError('universe', 'bStocks', 'BSTOCK_DISCOVERY_UNAVAILABLE', error)
    candidateUniverse = registryBStockUniverse(error, discoveredAt)
  }

  const statusSymbols = [
    ...candidateUniverse.items.map(item => item.symbol),
    ...MAINSTREAM_MARKETS.map(item => item.symbol)
  ]
  const [statusOutcome] = await Promise.allSettled([fetchTradingStatuses(statusSymbols, signal)])
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')

  const universe = verifyBStockUniverse(candidateUniverse, statusOutcome)
  const symbols = statusOutcome.status === 'fulfilled'
    ? [...universe.items.map(item => item.symbol), ...MAINSTREAM_MARKETS.map(item => item.symbol)]
    : statusSymbols
  const klineOutcomes = await Promise.allSettled(symbols.map(symbol => {
    const trading = statusOutcome.status === 'fulfilled' ? statusOutcome.value.get(symbol) : null
    if (!trading || trading.status !== 'TRADING' || !trading.isSpotTradingAllowed) return Promise.resolve(null)
    return fetchJson(
      `${BINANCE_MARKET_BASE}/api/v3/klines?symbol=${encodeURIComponent(symbol)}&interval=4h&limit=121`,
      signal
    )
  }))
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')

  const errors: StrategyRadarError[] = discoveryError ? [discoveryError] : []
  if (universe.warning && !discoveryError) {
    errors.push(radarError('universe', 'bStocks', 'BSTOCK_STATUS_UNAVAILABLE', universe.warning))
  }
  const resolved = symbols.map((symbol, index) => buildPublicMarketItem(
    symbol,
    statusOutcome,
    klineOutcomes[index]
  ))
  resolved.forEach(result => errors.push(...result.errors))
  const items = resolved.map(result => result.item)
  const availableCount = items.filter(item => item.status === 'available').length
  const status: StrategyRadarSectionStatus = availableCount === 0
    ? 'unavailable'
    : availableCount === items.length && errors.length === 0
      ? 'available'
      : 'partial'
  return {
    batch: {
      status,
      items,
      requestedCount: symbols.length,
      availableCount,
      fetchedAt: Date.now(),
      errors
    },
    universe
  }
}

function buildPublicMarketItem(
  symbol: string,
  statusOutcome: PromiseSettledResult<Map<string, TradingStatus>>,
  klineOutcome: PromiseSettledResult<unknown>
) {
  const errors: StrategyRadarError[] = []
  const tradingStatus = statusOutcome.status === 'fulfilled'
    ? statusOutcome.value.get(symbol)
    : undefined
  if (statusOutcome.status === 'rejected') {
    errors.push(radarError('market', symbol, 'TRADING_STATUS_UNAVAILABLE', statusOutcome.reason))
  } else if (!tradingStatus) {
    errors.push(radarError('market', symbol, 'TRADING_STATUS_UNAVAILABLE', '币安未返回该标的交易状态'))
  }
  if (klineOutcome.status === 'rejected') errors.push(radarError('market', symbol, 'KLINE_UNAVAILABLE', klineOutcome.reason))
  if (!tradingStatus || klineOutcome.status === 'rejected') {
    const warning = errors.map(item => item.message).join('；')
    return { item: unavailableMarketItem(symbol, warning), errors }
  }

  const bars = parseBinanceKlines(klineOutcome.value).slice(-120)
  const computed = guardSwingSignal(
    analyzeSwing(bars),
    tradingStatus.status,
    tradingStatus.isSpotTradingAllowed,
    Date.now()
  )
  const available = computed.state !== 'unavailable'
  if (!available) errors.push({
    component: 'market',
    subject: symbol,
    code: 'SIGNAL_GUARD_FAILED',
    message: computed.entryRule,
    retryable: true
  })
  const btcZone = symbol === 'BTCUSDT' && available ? btcRangeZone(computed.price) : null
  const item: RadarMarketResult = {
    symbol,
    status: available ? 'available' : 'unavailable',
    change24hPct: closedBarChange24h(bars),
    signal: computed,
    source: 'BINANCE_PUBLIC_REST',
    cacheStatus: 'no-store',
    isFallback: false,
    warning: available ? null : computed.entryRule,
    errorCode: available ? null : 'SIGNAL_GUARD_FAILED',
    btcZone: btcZone?.id ?? null,
    btcZoneLabel: btcZone?.label ?? null,
    btcZoneAction: btcZone?.action ?? null
  }
  return { item, errors }
}

async function fetchPublicListings(signal?: AbortSignal): Promise<RadarListingBatch> {
  const payload = await fetchJson(
    `${BINANCE_CMS_BASE}/article/list/query?type=1&pageNo=1&pageSize=20`,
    signal
  )
  const articles = announcementArticles(payload)
    .flatMap(article => {
      const kind = typeof article.title === 'string' ? classifyListingAnnouncement(article.title) : null
      return kind ? [{ ...article, kind }] : []
    })
    .sort((left, right) => (finiteNumber(right.releaseDate) ?? 0) - (finiteNumber(left.releaseDate) ?? 0))
    .slice(0, 12)
  const outcomes = await Promise.all(articles.map(async article => {
    const code = String(article.code)
    const title = String(article.title)
    const kind = article.kind
    const scheduleUpdate = isListingScheduleUpdate(title)
    let detail = announcementDetailCache.get(code)
    if (detail && Date.now() - detail.fetchedAt > ANNOUNCEMENT_DETAIL_TTL_MS) detail = undefined
    const warnings = detail?.warning ? [detail.warning] : []
    if (kind === 'other' && !scheduleUpdate) warnings.push('公告类型待人工核验')
    if (!detail) {
      try {
        const detailPayload = await fetchJson(
          `${BINANCE_CMS_BASE}/article/detail/query?articleCode=${encodeURIComponent(code)}`,
          signal
        )
        const body = objectValue(objectValue(detailPayload, 'data'), 'body')
        if (body == null) throw new Error('Binance 公告正文响应缺少 body')
        const text = articlePlainText(body)
        const cancelled = scheduleUpdate && isListingScheduleCancellation(title, text)
        const tradingAt = scheduleUpdate ? extractScheduleUpdateTradingAt(text) : extractTradingAt(text)
        const symbols = extractSpotSymbols(text, title)
        const unresolved = scheduleUpdate
          ? [
              !cancelled && tradingAt == null ? '上市变更公告未提取到新 UTC 时间' : '',
              symbols.length ? '' : '上市变更公告未提取到现货交易对'
            ].filter(Boolean)
          : kind === 'spot_listing' ? [
              tradingAt == null ? '公告正文未提取到明确 UTC 交易时间' : '',
              symbols.length ? '' : '公告正文未提取到现货交易对'
            ].filter(Boolean)
          : []
        const detailWarning = unresolved.length ? unresolved.join('；') : null
        if (detailWarning) warnings.push(detailWarning)
        detail = {
          tradingAt,
          symbols,
          warning: detailWarning,
          cancelled,
          fetchedAt: Date.now()
        }
        announcementDetailCache.set(code, detail)
      } catch (error) {
        if (signal?.aborted) throw error
        const detailWarning = errorMessage(error)
        warnings.push(detailWarning)
        detail = {
          tradingAt: null,
          symbols: kind === 'spot_listing' || scheduleUpdate ? extractSpotSymbols('', title) : [],
          warning: detailWarning,
          cancelled: false,
          fetchedAt: Date.now()
        }
      }
    }
    const hasExplicitSpotSchedule = (
      kind !== 'alpha'
      && (kind !== 'other' || scheduleUpdate)
      && !detail.cancelled
      && detail.tradingAt != null
      && detail.symbols.length > 0
    )
    const tradingAt = hasExplicitSpotSchedule ? detail.tradingAt : null
    const warning = [...new Set(warnings)].join('；') || null
    const item: RadarListingResult = {
      code,
      title,
      releaseAt: finiteNumber(article.releaseDate) ?? 0,
      articleUrl: `https://www.binance.com/en/support/announcement/detail/${encodeURIComponent(code)}`,
      tradingAt,
      symbols: detail.symbols,
      status: warning || detail.cancelled ? 'partial' : 'available',
      warning: detail.cancelled ? appendWarning(warning, '官方公告已取消或阻断原计划时钟。') : warning,
      // A verified reschedule is still the same Spot listing. Keeping it as
      // spot_listing allows the T+3–15 1m gate to follow the replacement
      // clock; cancelled or unresolved updates remain blocked as `other`.
      kind: scheduleUpdate && hasExplicitSpotSchedule ? 'spot_listing' : kind,
      kindLabel: scheduleUpdate ? '上市变更' : listingKindLabel(kind),
      entryConfirmation: listingEntryConfirmation([], tradingAt, null, null, Date.now())
    }
    return { item, warning: item.warning, scheduleUpdate }
  }))
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
  const normalizedOutcomes = coalesceListingScheduleUpdates(outcomes)
  const detailErrors = normalizedOutcomes.flatMap(({ item, warning }) => warning
    ? [radarError('listing', item.code, 'ANNOUNCEMENT_DETAIL_UNAVAILABLE', warning)]
    : [])
  const confirmation = await attachListingEntryConfirmations(
    normalizedOutcomes.map(outcome => outcome.item),
    signal
  )
  const errors = [...detailErrors, ...confirmation.errors]
  return {
    status: errors.length ? 'partial' : 'available',
    items: confirmation.items,
    source: 'BINANCE_CMS_PUBLIC_FEED',
    cacheStatus: 'browser-ttl',
    fetchedAt: Date.now(),
    errors
  }
}

function coalesceListingScheduleUpdates<T extends {
  item: RadarListingResult
  warning: string | null
  scheduleUpdate: boolean
}>(outcomes: T[]): T[] {
  const blockedByNewerUpdate = new Set<string>()
  const result: T[] = []
  const ordered = [...outcomes].sort((left, right) => right.item.releaseAt - left.item.releaseAt)
  for (const outcome of ordered) {
    const symbols = outcome.item.symbols.filter(symbol => /^[A-Z0-9]{2,14}(?:USDT|USDC|FDUSD)$/.test(symbol))
    if (outcome.scheduleUpdate) {
      if (symbols.some(symbol => blockedByNewerUpdate.has(symbol))) continue
      symbols.forEach(symbol => blockedByNewerUpdate.add(symbol))
      result.push(outcome)
      continue
    }
    if (symbols.some(symbol => blockedByNewerUpdate.has(symbol))) continue
    result.push(outcome)
  }
  return result.slice(0, 6)
}

async function attachListingEntryConfirmations(
  items: RadarListingResult[],
  signal?: AbortSignal
): Promise<{ items: RadarListingResult[]; errors: StrategyRadarError[] }> {
  const now = Date.now()
  const eligibleByItem = new Map<string, string>()
  for (const item of items) {
    if (
      item.kind === 'alpha'
      || item.kind === 'other'
      || listingPhase(item.tradingAt, now).state !== 'entry-window'
    ) continue
    const symbol = item.symbols.find(value => /^[A-Z0-9]{2,14}USDT$/.test(value))
    if (symbol) eligibleByItem.set(item.code, symbol)
  }
  const symbols = [...new Set(eligibleByItem.values())].slice(0, MAX_LISTING_CONFIRMATION_SYMBOLS)
  if (!symbols.length) return { items, errors: [] }

  let statuses: Map<string, TradingStatus>
  try {
    statuses = await fetchTradingStatuses(symbols, signal)
  } catch (error) {
    if (signal?.aborted) throw error
    const message = errorMessage(error)
    return {
      items: items.map(item => eligibleByItem.has(item.code)
        ? withEntryConfirmationError(item, message)
        : item),
      errors: symbols.map(symbol => radarError(
        'listing',
        symbol,
        'ENTRY_TRADING_STATUS_UNAVAILABLE',
        error
      ))
    }
  }

  const klineSymbols = symbols.filter(symbol => {
    const trading = statuses.get(symbol)
    return trading?.status === 'TRADING' && trading.isSpotTradingAllowed
  })
  const outcomes = await Promise.allSettled(klineSymbols.map(symbol => fetchJson(
    `${BINANCE_MARKET_BASE}/api/v3/klines?symbol=${encodeURIComponent(symbol)}&interval=1m&limit=20`,
    signal
  )))
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
  const outcomeBySymbol = new Map(klineSymbols.map((symbol, index) => [symbol, outcomes[index]]))
  const errors: StrategyRadarError[] = []
  const confirmedItems = items.map(item => {
    const symbol = eligibleByItem.get(item.code)
    if (!symbol) return item
    if (!symbols.includes(symbol)) return {
      ...item,
      entryConfirmation: unavailableEntryConfirmation(
        '本轮最多核验 2 个上线 USDT 标的，该标的未进入 1m 请求队列。'
      )
    }
    const trading = statuses.get(symbol)
    const outcome = outcomeBySymbol.get(symbol)
    if (!outcome) return {
      ...item,
      entryConfirmation: listingEntryConfirmation(
        [],
        item.tradingAt,
        trading?.status ?? null,
        trading?.isSpotTradingAllowed ?? null,
        now
      )
    }
    if (outcome.status === 'rejected') {
      const message = errorMessage(outcome.reason)
      errors.push(radarError('listing', symbol, 'ENTRY_KLINE_UNAVAILABLE', outcome.reason))
      return withEntryConfirmationError(item, message)
    }
    const confirmation = listingEntryConfirmation(
      parseBinanceKlines(outcome.value, now),
      item.tradingAt,
      trading?.status ?? null,
      trading?.isSpotTradingAllowed ?? null,
      now
    )
    if (confirmation.state === 'unavailable') {
      errors.push(radarError('listing', symbol, 'ENTRY_CONFIRMATION_GUARD_FAILED', confirmation.summary))
      return {
        ...item,
        status: 'partial' as const,
        warning: appendWarning(item.warning, confirmation.summary),
        entryConfirmation: confirmation
      }
    }
    return { ...item, entryConfirmation: confirmation }
  })
  return { items: confirmedItems, errors }
}

function verifyBStockUniverse(
  candidate: RadarBStockUniverse,
  statusOutcome: PromiseSettledResult<Map<string, TradingStatus>>
): RadarBStockUniverse {
  if (statusOutcome.status === 'rejected') return {
    ...candidate,
    status: 'unavailable',
    items: [],
    selectedCount: 0,
    warning: appendWarning(candidate.warning, '批量 exchangeInfo 未返回可验证的现货交易权限。')
  }
  const items = candidate.items.filter(item => {
    const trading = statusOutcome.value.get(item.symbol)
    return trading?.status === 'TRADING' && trading.isSpotTradingAllowed
  })
  const rejectedCount = candidate.items.length - items.length
  return {
    ...candidate,
    status: items.length === 0
      ? 'unavailable'
      : candidate.status === 'partial' || rejectedCount > 0
        ? 'partial'
        : 'available',
    items,
    selectedCount: items.length,
    warning: rejectedCount
      ? appendWarning(candidate.warning, `${rejectedCount} 个候选未同时通过 TRADING 与 isSpotTradingAllowed 门禁。`)
      : candidate.warning
  }
}

function registryBStockUniverse(error: unknown, fetchedAt: number): RadarBStockUniverse {
  return {
    status: 'partial',
    items: STOCK_MAPPINGS.map(item => ({
      symbol: item.symbol,
      token: item.token,
      fullName: `${item.company} (bStocks)`,
      company: item.company,
      underlying: item.underlying,
      theme: item.theme,
      volume: 0,
      registryStatus: 'registered' as const
    })),
    discoveredCount: 0,
    selectedCount: STOCK_MAPPINGS.length,
    limit: MAX_BSTOCK_MARKETS,
    truncated: false,
    source: 'STATIC_OFFICIAL_REGISTRY',
    fetchedAt,
    warning: `动态 bStocks 发现不可用，本轮仅校验官方静态 registry：${errorMessage(error)}`
  }
}

function unavailableBStockUniverse(error: unknown, fetchedAt: number): RadarBStockUniverse {
  return {
    status: 'unavailable',
    items: [],
    discoveredCount: 0,
    selectedCount: 0,
    limit: MAX_BSTOCK_MARKETS,
    truncated: false,
    source: null,
    fetchedAt,
    warning: errorMessage(error)
  }
}

function withEntryConfirmationError(item: RadarListingResult, message: string): RadarListingResult {
  return {
    ...item,
    status: 'partial',
    warning: appendWarning(item.warning, message),
    entryConfirmation: unavailableEntryConfirmation(message)
  }
}

function unavailableEntryConfirmation(summary: string): RadarEntryConfirmation {
  return {
    state: 'unavailable',
    label: '1m 数据待核验',
    tone: 'neutral',
    summary,
    dataTime: null
  }
}

function appendWarning(current: string | null, next: string) {
  return [...new Set([current, next].filter((value): value is string => Boolean(value)))].join('；') || null
}

function listingKindLabel(kind: RadarListingResult['kind']) {
  return {
    spot_listing: '现货上市',
    launchpool: 'Launchpool',
    launchpad: 'Launchpad',
    megadrop: 'Megadrop',
    airdrop: '空投',
    alpha: 'Binance Alpha',
    other: '类型待核验'
  }[kind]
}

async function fetchTradingStatuses(symbols: string[], signal?: AbortSignal) {
  const now = Date.now()
  const statuses = new Map<string, TradingStatus>()
  const missing = symbols.filter(symbol => {
    const cached = tradingStatusCache.get(symbol)
    if (!cached || now - cached.fetchedAt > TRADING_STATUS_TTL_MS) return true
    statuses.set(symbol, {
      status: cached.status,
      isSpotTradingAllowed: cached.isSpotTradingAllowed
    })
    return false
  })
  if (!missing.length) return statuses
  const payload = await fetchJson(
    `${BINANCE_MARKET_BASE}/api/v3/exchangeInfo?symbols=${encodeURIComponent(JSON.stringify(missing))}`,
    signal
  )
  const rows = objectValue(payload, 'symbols')
  if (!Array.isArray(rows)) throw new Error('币安未返回交易状态列表')
  rows.forEach(row => {
    const symbol = objectValue(row, 'symbol')
    const status = objectValue(row, 'status')
    const isSpotTradingAllowed = objectValue(row, 'isSpotTradingAllowed')
    if (typeof symbol !== 'string' || typeof status !== 'string' || typeof isSpotTradingAllowed !== 'boolean') return
    const trading = { status, isSpotTradingAllowed }
    statuses.set(symbol, trading)
    tradingStatusCache.set(symbol, { ...trading, fetchedAt: now })
  })
  return statuses
}

function announcementArticles(payload: unknown): BinanceArticle[] {
  const catalogs = objectValue(objectValue(payload, 'data'), 'catalogs')
  if (!Array.isArray(catalogs)) throw new Error('Binance 公告列表响应缺少 catalogs')
  return catalogs.flatMap(catalog => {
    const articles = objectValue(catalog, 'articles')
    if (!Array.isArray(articles)) throw new Error('Binance 公告列表响应缺少 articles')
    return articles.map(article => {
      if (!article || typeof article !== 'object') {
        throw new Error('Binance 公告列表包含无效 article')
      }
      const item = article as BinanceArticle
      if (typeof item.code !== 'string' || !item.code || typeof item.title !== 'string' || !item.title.trim()) {
        throw new Error('Binance 公告列表 article 缺少 code 或 title')
      }
      return item
    })
  })
}

export function closedBarChange24h(bars: Array<{ close: number }>) {
  if (bars.length < 7 || bars[bars.length - 7].close <= 0) return null
  const change = (bars[bars.length - 1].close / bars[bars.length - 7].close - 1) * 100
  return Number(change.toFixed(4))
}

async function fetchJson(url: string, signal?: AbortSignal): Promise<unknown> {
  const controller = new AbortController()
  let timedOut = false
  const abortFromParent = () => controller.abort(signal?.reason)
  if (signal?.aborted) abortFromParent()
  else signal?.addEventListener('abort', abortFromParent, { once: true })
  const timeout = globalThis.setTimeout(() => {
    timedOut = true
    controller.abort()
  }, 12_000)
  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      credentials: 'omit'
    })
    if (!response.ok) throw new Error(`Binance 公开数据请求失败 (${response.status})`)
    return response.json()
  } catch (error) {
    if (timedOut) throw new Error('Binance 公开数据请求超时（12 秒）')
    throw error
  } finally {
    globalThis.clearTimeout(timeout)
    signal?.removeEventListener('abort', abortFromParent)
  }
}

function unavailableMarketBatch(error: unknown, fetchedAt: number): RadarMarketBatch {
  const symbols = [...STOCK_MAPPINGS, ...MAINSTREAM_MARKETS].map(item => item.symbol)
  return {
    status: 'unavailable',
    items: symbols.map(symbol => unavailableMarketItem(symbol, errorMessage(error))),
    requestedCount: symbols.length,
    availableCount: 0,
    fetchedAt,
    errors: [radarError('market', 'all', 'MARKET_BATCH_UNAVAILABLE', error)]
  }
}

function unavailableListingBatch(error: unknown, fetchedAt: number): RadarListingBatch {
  return {
    status: 'unavailable',
    items: [],
    source: 'BINANCE_CMS_PUBLIC_FEED',
    cacheStatus: 'error',
    fetchedAt,
    errors: [radarError('listing', 'feed', 'LISTING_FEED_UNAVAILABLE', error)]
  }
}

function unavailableMarketItem(symbol: string, warning: string): RadarMarketResult {
  return {
    symbol,
    status: 'unavailable',
    change24hPct: null,
    signal: {
      state: 'unavailable',
      label: '数据待核验',
      tone: 'neutral',
      outlook: '待核验',
      price: null,
      ema20: null,
      ema50: null,
      rsi14: null,
      rangeLow: null,
      rangeHigh: null,
      entryRule: warning || '当前数据源未返回可验证的 4H 规则信号。',
      exitRule: '数据不可用时不生成卖出或持有提示。',
      reasons: [warning || '公开行情不可用'],
      momentum: [],
      dataTime: null,
      latestPrice: null,
      latestPriceAsOf: null,
      trendForecast: 'unavailable',
      forecastSummary: '数据门禁未通过，不预测未来 1–3 根已闭合 4H。',
      forecastHorizon: 'NEXT_1_TO_3_CLOSED_4H',
      forecastConfidence: 'unavailable',
      positionTier: 'none',
      positionGuidance: '不生成仓位动作，等待数据恢复。'
    },
    source: null,
    cacheStatus: 'error',
    isFallback: false,
    warning: warning || null,
    errorCode: 'MARKET_UNAVAILABLE',
    btcZone: null,
    btcZoneLabel: null,
    btcZoneAction: null
  }
}

function radarError(component: string, subject: string, code: string, error: unknown): StrategyRadarError {
  return {
    component,
    subject,
    code,
    message: typeof error === 'string' ? error : errorMessage(error),
    retryable: true
  }
}

function finiteNumber(value: unknown): number | null {
  if (value == null || value === '') return null
  const number = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(number) ? number : null
}

function objectValue(value: unknown, key: string): unknown {
  return value && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : String(error)
}
